import React, { use, useEffect } from 'react'

import { useAuthState } from 'react-firebase-hooks/auth'
import { Spinner } from '@chakra-ui/react'
import * as Sentry from '@sentry/react'

import { auth } from '@/firebase'
import { useTeam } from '@/react-query/team'
import { useAuthListener, useUser } from '@/react-query/user'

import { IAuthContext } from './types'

export const AuthContext = React.createContext<IAuthContext | undefined>(
  undefined,
)

const signInLocations = ['/sign-in', '/sign-up']

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [, authLoading] = useAuthState(auth)
  const { data: databaseData, isLoading: isDBLoading } = useUser()
  const team = useTeam()
  const shouldBypassAuth =
    !import.meta.env.PROD &&
    (import.meta.env.MODE === 'e2e' ||
      import.meta.env.VITE_E2E_AUTH_BYPASS === 'true' ||
      new URLSearchParams(window.location.search).has('e2e-auth-bypass'))

  const isLoading = isDBLoading || authLoading || team.isLoading

  useAuthListener()

  useEffect(() => {
    if (shouldBypassAuth) {
      return
    }

    if (databaseData) {
      window.Intercom('boot', {
        api_base: 'https://api-iam.intercom.io',
        app_id: 'vl0cn6i6',
        name: databaseData.displayName || '',
        email: databaseData.email,
        created_at:
          new Date(databaseData.createdAt || Date.now()).getTime() / 1000,
      })
      Sentry.setUser({
        email: databaseData.email,
        name: databaseData.displayName || '',
      })
    } else {
      window.Intercom('shutdown')
      Sentry.setUser(null)
    }
  }, [databaseData, shouldBypassAuth])

  if (
    !shouldBypassAuth &&
    !signInLocations.includes(window.location.pathname) &&
    isLoading
  ) {
    return (
      <div className="flex flex-grow">
        <Spinner className="m-auto w-10 h-10" />
      </div>
    )
  }

  return (
    <AuthContext
      value={{
        isLoading: shouldBypassAuth ? false : isLoading,
      }}
    >
      {children}
    </AuthContext>
  )
}

export function useAuth() {
  const ctx = use(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>')
  return ctx
}
